import React from "react";
import PropTypes from "prop-types";
import numeral from "numeral";
import { legendOptions, defaultMapOptions, mapTooltipOptions } from "../map/options";
import mapData from "../data/mapData";

const HighchartsReact = window.HighchartsReact;
const highcharts = window.Highcharts;

const getNRWMapData = data => {
  return data.map(country => [country.country_id, country.nrw_average]);
};

const getPrivatisationMapData = data => {
  return data.map(country => [country.country_id, country.extent_of_privatisation]);
};

const getFragmentationMapData = data => {
  return data.map(country => [country.country_id, country.sheet_utilities_total]);
};

class Map extends React.Component {
  constructor(props) {
    super(props);
  }

  shouldComponentUpdate() {
    return false;
  }

  render () {
    const renderUtilityAccuracy = (id) => {
      const country = this.props.countries.find(country => country.country_id === id);
      return country.sheet_utilities_accuracy;
    };
    const renderNRWTooltip = (point) => {
      const country = this.props.countries.find(country => country.country_id === point["hc-key"]);
      const { nrw_data_source, country_nrw_percentage_year, country_id } = country;
      if (nrw_data_source === "Country" && point.value !== -1) {
        return `${point.value}% NRW average ${country_nrw_percentage_year && `(${country_nrw_percentage_year})`}`;
      } else if (nrw_data_source === "Utilities" || country_id == "us") {
        return `${point.value}% NRW average (GWI estimate)`;
      } else if (nrw_data_source === null) {
        return "-% NRW average";
      }
    };
    const mapOptions = {
      ...defaultMapOptions,
      legend: {
        ...legendOptions
      },
      plotOptions: {
        series: {
          point: {
            events: {
              click: event => this.props.selectFromMap(event.point["hc-key"])
            }
          }
        }
      },
      colorAxis: [
        {
          showInLegend: false,
          dataClasses: [{
            from: -1,
            to: 0,
            color: "#7F8982",
            "name": "No data"
          },{
            from: 0.1,
            to: 10,
            color: "#d7e4a8",
            "name": "0-10%"
          },{
            from: 10,
            to: 20,
            color: "#b9d263",
            "name": "10-20%"
          },{
            from: 20,
            to: 30,
            color: "#a0ba99",
            "name": "20-30%"
          }, {
            from: 30,
            to: 40,
            color: "#7da579",
            "name": "30-40%"
          }, {
            from: 40,
            to: 50,
            color: "#54925b",
            "name": "40-50%"
          }, {
            from: 50,
            color: "#214525",
            "name": "50%+"
          }]
        },
        {
          showInLegend: false,
          dataClasses: [{
            from: 0,
            to: 0,
            color: "#7F8982",
            "name": "No data"
          }, {
            from: 1,
            to: 1,
            color: "#df9cc5",
            "name": "1"
          }, {
            from: 2,
            to: 30,
            color: "#d576ad",
            "name": "2-30"
          }, {
            from: 30,
            to: 100,
            color: "#b484ac",
            "name": "30-100"
          }, {
            from: 100,
            to: 1000,
            color: "#9e5b91",
            "name": "100-1000"
          }, {
            from: 1000,
            to: 5000,
            color: "#89357a",
            "name": "1000-5000"
          }, {
            from: 5000,
            color: "#3b1334",
            "name": "5000+"
          }]
        },
        {
          showInLegend: false,
          dataClasses: [{
            from: 0,
            to: 0,
            color: "#f4b446",
            "name": "0%"
          }, {
            from: 0.1,
            to: 25,
            color: "#df8f80",
            "name": "0-25%"
          }, {
            from: 25,
            to: 50,
            color: "#d4675c",
            "name": "25-50%"
          }, {
            from: 50,
            to: 75,
            color: "#ca3d3c",
            "name": "50-75%"
          }, {
            from: 75,
            color: "#7e1a1a",
            "name": "75%+"
          }]
        },
      ],
      series: [
        {
          showInLegend: false,
          name: "NRW",
          mapData,
          data: getNRWMapData(this.props.countries),
          nullColor: "#999E98",
          borderWidth: 1,
          states: {
            select: {
              color: "#2067a3",
              borderColor: "#acacac",
            }
          },
          colorAxis: 0,
          cursor: "pointer",
        },
        {
          showInLegend: false,
          name: "Fragmentation",
          visible: false,
          mapData,
          data: getFragmentationMapData(this.props.countries),
          nullColor: "#999E98",
          borderWidth: 1,
          states: {
            select: {
              color: "#2067a3",
              borderColor: "#acacac",
            }
          },
          colorAxis: 1,
          cursor: "pointer",
        },
        {
          showInLegend: false,
          name: "Privatisation",
          visible: false,
          mapData,
          data: getPrivatisationMapData(this.props.countries),
          nullColor: "#999E98",
          borderWidth: 1,
          states: {
            select: {
              color: "#2067a3",
              borderColor: "#acacac",
            }
          },
          colorAxis: 2,
          cursor: "pointer",
        }
      ],
      tooltip: {
        formatter: function() {
          let content = "";
          if (this.point.series.name === "NRW") {
            content = renderNRWTooltip(this.point);
          } else if (this.point.series.name === "Fragmentation") {
            let accuracy = renderUtilityAccuracy(this.point["hc-key"]);
            let preAccuracy = "";
            let postAccuracy = "";
            if (accuracy === "Estimate") {
              preAccuracy = "circa";
            }
            if (accuracy === "At least") {
              postAccuracy = "+";
            }
            content = this.point.value === 0
                      ? "-"
                      : `${preAccuracy} ${numeral(this.point.value).format("0,0")}${postAccuracy} utilities`;
          } else if (this.point.series.name === "Privatisation") {
            content = `${this.point.value}% private`;
          }
          return `
            <div style="box-shadow: 0 0 4px 1px rgba(0, 0, 0, 0.3);">
              <div style="background-color: white; padding: 10px; border-top: 8px solid ${this.color}">
              <b>${this.key}</b></br>
              <div>${content}</div>
              </div>
            </div>
          `;
        },
        ...mapTooltipOptions,
      },
      title: {
        align: "left",
        style: { color: "#0065a4", textTransform: "uppercase" }
      },
    };

    return (
      <HighchartsReact
        highcharts={highcharts}
        constructorType={"mapChart"}
        options={mapOptions}
        callback={this.props.setChart}
      />
    );
  }
}

Map.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectFromMap: PropTypes.func.isRequired,
  setChart: PropTypes.func,
  currentType: PropTypes.string.isRequired
};

class MapContainer extends React.PureComponent {
  constructor(props) {
    super(props);

    this.mutateMap = this.mutateMap.bind(this);
    this.setChart = this.setChart.bind(this);
  }

  componentDidUpdate() {
    this.mutateMap();
  }

  setChart(chart) {
    this.chart = chart;

    this.mutateMap();
  }

  mutateMap() {
    const selectedSeries = this.props.currentType;
    const code = this.props.selectedCountryCode;

    const activateSeriesIndex = {
      nrw_average: 0,
      sheet_utilities_total: 1,
      extent_of_privatisation: 2
    }[selectedSeries];

    this.chart.series.forEach((series, i) => {
      if (i === activateSeriesIndex) {
        series.update({ visible: true });

        const { data } = series;
        if (code === "all") {
          data.map(country => country.selected && country.update({ selected: false }));
        } else {
          data.map(country => {
            if (country["hc-key"] === code) {
              country.update({ selected: true });
            } else if (country["hc-key"] !== code && country.selected === true) {
              country.update({ selected: false });
            }
            return country;
          });
        }

        this.chart.colorAxis[i].update({ showInLegend: true });

        const renderMapTitle = val => {
          let title;
          if (val === 0) {
            title = "Average NRW rate by country";
          } if (val === 1) {
            title = "Total number of utilities by country";
          } if (val === 2) {
            title = "Percentage of major utilities privatised/part-privatised/under concession";
          }
          return title;
        };
        this.chart.setTitle({ text: renderMapTitle(i) });

        const setLegendTitle = val => {
          if (val === 0) {
            return "% NRW average";
          } if (val === 1) {
            return "Number of utilities";
          } if (val === 2) {
            return "% private";
          }
        };
        this.chart.series[i].chart.legend.update({ title: { text: setLegendTitle(i) } });
      } else {
        series.update({ visible: false });
        this.chart.colorAxis[i].update({ showInLegend: false });
      }
    });
  }

  render() {
    return (
      <Map
        setChart={this.setChart}
        countries={this.props.countries}
        selectFromMap={this.props.selectFromMap}
        currentType={this.props.currentType}
      />
    );
  }
}

MapContainer.propTypes = {
  countries: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  selectFromMap: PropTypes.func.isRequired,
  selectedCountryCode: PropTypes.string,
  currentType: PropTypes.string,
};

export default MapContainer;

